import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Building, User, RefreshCw, Plus, Database, BarChart3 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSupabaseTeams, useTeamsStats } from "@/hooks/useSupabaseTeams";
import { useCompanyNameByLegacyId } from "@/hooks/useSupabaseCompanies";
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";
import { format } from 'date-fns';

// Company name display component
const CompanyDisplay = ({ compId }: { compId?: string }) => {
  const companyName = useCompanyNameByLegacyId(compId);
  
  if (!compId) {
    return <span className="text-muted-foreground">-</span>;
  }
  
  return (
    <Badge variant="outline" className="w-fit">
      <Building className="h-3 w-3 mr-1" />
      {companyName}
    </Badge>
  );
};

const Teams = () => {
  // Use Supabase teams with pagination
  const { 
    data: teamsResponse, 
    isLoading: loading, 
    error, 
    refetch: refresh 
  } = useSupabaseTeams({}, { limit: 100 });

  // Get teams statistics
  const { 
    data: stats, 
    isLoading: statsLoading 
  } = useTeamsStats();

  const { toast } = useToast();

  // Extract teams from response
  const teams = teamsResponse?.data || [];

  if (loading) {
    return <PageLoader type="table" title="Đang tải dữ liệu teams..." />;
  }

  if (error) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <p className="text-destructive mb-4">Lỗi: {error}</p>
              <Button onClick={refresh} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Thử lại
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Quản lý Teams</h1>
          <p className="text-xl text-muted-foreground">Quản lý và theo dõi các team trong tổ chức</p>
        </div>

        {/* Statistics Cards */}
        {stats && !statsLoading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="flex items-center p-6">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Teams</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalTeams}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <Building className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Units</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.uniqueUnits}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <BarChart3 className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Companies</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.uniqueCompanies}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <User className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">With Managers</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.teamsWithManagers}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-blue-600" />
                  Teams từ Supabase ({teams.length})
                </CardTitle>
                {stats?.latestSyncDate && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Sync lần cuối: {format(new Date(stats.latestSyncDate), 'dd/MM/yyyy HH:mm')}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={refresh} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Làm mới
                </Button>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Thêm Team
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead>Mô tả</TableHead>
                    <TableHead>Unit/Company</TableHead>
                    <TableHead>Report Prefix</TableHead>
                    <TableHead>Managers</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => (
                    <TableRow key={team.id}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {team.team_name || 'N/A'}
                          </span>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">Legacy: {team.legacy_id}</span>
                            <Badge variant="outline" className="text-xs">
                              Supabase
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {team.description ? (
                          <p className="text-sm line-clamp-2">{team.description}</p>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {team.unit_id && (
                            <Badge variant="secondary" className="w-fit">
                              <Building className="h-3 w-3 mr-1" />
                              Unit: {team.unit_id}
                            </Badge>
                          )}
                          <CompanyDisplay compId={team.comp_id} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {team.report_name_prefix ? (
                          <Badge variant="secondary">{team.report_name_prefix}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {team.manager_users ? (
                          <div className="flex items-start gap-1">
                            <User className="h-3 w-3 mt-0.5 text-blue-500" />
                            <p className="text-sm text-blue-600 line-clamp-2">{team.manager_users}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {team.member_users ? (
                          <div className="flex items-start gap-1">
                            <Users className="h-3 w-3 mt-0.5 text-green-500" />
                            <p className="text-sm text-green-600 line-clamp-2">
                              {team.member_users.length > 50 
                                ? `${team.member_users.substring(0, 50)}...` 
                                : team.member_users
                              }
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              toast({
                                title: "Edit Team",
                                description: `Editing team ${team.team_name || team.legacy_id}`,
                              });
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              toast({
                                title: "View Reports", 
                                description: `Viewing reports for team ${team.team_name || team.legacy_id}`,
                              });
                            }}
                          >
                            Reports
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {teams.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Không có team nào</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </PageTransition>
  );
};

export default Teams;
